import cn from 'classnames';
import { useContext, useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';

import Button from 'components/common/Button';
import Text from 'components/common/Text';
import { UserContext } from 'components/signUpFunnel/Contexts';
import * as Routes from 'helpers/routes';
import useAnalyticsCallback from 'hooks/useAnalyticsCallback';
import useScreenSizeConstants from 'hooks/useScreenSizeConstants';
import { TEMPO } from 'lib/constants';
import * as ErrorLogging from 'services/ErrorLogging';
import RequestService from 'services/RequestService';

const RecommendedBoxSummary = ({ weeklyMeals, showBoxOptions }) => {
  const [box, setBox] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user, setUser } = useContext(UserContext);
  const { isMobile } = useScreenSizeConstants();

  useEffect(() => {
    if (!weeklyMeals || !user) return;

    const fetchBox = async () => {
      try {
        const response = await RequestService.getBox({
          params: {
            servings: 2,
            weeklyMeals,
            zipCode: user.signupZip,
            estimated: true,
          },
        });
        if (response.ok) {
          setBox(response.box);
        } else {
          ErrorLogging.captureInfo(response.error);
        }
      } catch (error) {
        ErrorLogging.captureException(error.message);
      }
    };

    fetchBox();
  }, [weeklyMeals, user]);

  const handleNavigateToPickMeals = useAnalyticsCallback(
    async () => {
      setIsSubmitting(true);
      try {
        const response = await RequestService.updateBox({
          params: { servings: 2, weeklyMeals, isPickMeals: true },
        });

        if (response.ok) {
          setUser({ ...user, ...response.user });
          window.location.href = Routes.PICK_MEALS_MENU_PATH;
        } else {
          ErrorLogging.captureException(response.error);
        }

        return response;
      } catch (error) {
        ErrorLogging.captureException(error.message);
        return { ok: false, error: error.message };
      } finally {
        setIsSubmitting(false);
      }
    },
    ({ logEvent, events }) => {
      logEvent(events.pickMeals[TEMPO].boxRecommender.submitted, {
        mealPlanSlug: TEMPO,
        selectedBoxSize: weeklyMeals,
      });
    },
    [user, weeklyMeals]
  );

  return (
    <div
      className={cn('flex items-center justify-between', {
        'border-t border-solid border-charcoal-60 bg-white-100 px-4 pb-6 pt-8': isMobile,
        'mx-auto w-128 rounded-lg bg-tempo-gray-40 pb-12 pt-4': !isMobile,
        'shadow-[0_-17px_20px_-24px_rgba(0,0,0,0.25)]': !isMobile && showBoxOptions,
      })}
    >
      <div>
        {box ? (
          box?.promoApplied ? (
            <div className="flex flex-col">
              <Text brandStylesOverrides="h7 text-tempo-black-100 uppercase">{`${box.promo.displayedDiscount} THIS BOX ${box.promo.maxDiscount ? `(UP TO ${box.promo.maxDiscount})` : ''}`}</Text>
              <div className="flex items-center gap-2">
                <Text htmlElement="span" brandStylesOverrides="h1 text-tempo-navy-100">
                  {box.promo.subtotalFormatted}
                </Text>
                <Text
                  htmlElement="span"
                  className="line-through"
                  brandStylesOverrides="text-charcoal-90 h5"
                >
                  {box.subtotalFormatted}
                </Text>
              </div>
            </div>
          ) : (
            <>
              <Text brandStylesOverrides="h7 text-tempo-black-100">SUBTOTAL</Text>
              <Text brandStylesOverrides="h1 text-tempo-navy-100">{box?.subtotalFormatted}</Text>
            </>
          )
        ) : (
          <div className="flex flex-col gap-2">
            <Skeleton height={15} width={200} />
            <Skeleton height={48} width={200} />
          </div>
        )}
      </div>
      <div>
        <Button variation="Pill" onClick={handleNavigateToPickMeals} isSubmitting={isSubmitting}>
          Pick Meals
        </Button>
      </div>
    </div>
  );
};

RecommendedBoxSummary.propTypes = {
  weeklyMeals: PropTypes.number.isRequired,
  showBoxOptions: PropTypes.bool.isRequired,
};

export default RecommendedBoxSummary;
